import type { Product } from '@clockwise/clients/products';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customerActivitiesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  blankProductValues,
  handleProductMutationError,
  ProductFormFields,
  ProductFormSchema,
  type ProductFormValues,
  productPayloadFromValues,
} from './product-form-fields';

// Add/edit a customer-specific activity. Mirrors ProductFormSheet (shares the
// fields, payload builder, and field-error mapping) but every write is nested
// under the owning customer via `customerActivitiesApi`. The activity's
// `customerId` is fixed by the route — it never appears in the form and is
// immutable after creation. `internal` is likewise fixed to the customer's own
// flag (a custom activity is internal iff its customer is), so the "Kind"
// toggle is hidden and set programmatically.
interface CustomerActivityFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** The owning customer's internal flag — pins the activity's `internal`. */
  customerIsInternal: boolean;
  /** The activity being edited, or null for an add. */
  activity: Product | null;
}

export function CustomerActivityFormSheet({
  open,
  onOpenChange,
  customerId,
  customerIsInternal,
  activity,
}: CustomerActivityFormSheetProps) {
  const isEdit = activity !== null;
  const queryClient = useQueryClient();

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema),
    defaultValues: blankProductValues,
  });

  useEffect(() => {
    if (!open) return;
    if (activity) {
      form.reset({
        retainerCode: activity.retainerCode ?? '',
        tmCode: activity.tmCode ?? '',
        name: activity.name,
        description: activity.description ?? '',
        serviceRoleId: activity.serviceRoleId,
        visibleToServiceRoleIds: activity.visibleToServiceRoleIds,
        // `internal` follows the customer, not the stored value — this also
        // heals any legacy activity whose flag drifted from its customer.
        internal: customerIsInternal,
        excludeFromBudget: activity.excludeFromBudget,
      });
    } else {
      form.reset({ ...blankProductValues, internal: customerIsInternal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id, customerIsInternal]);

  const mutation = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const body = productPayloadFromValues(values);
      return isEdit && activity
        ? customerActivitiesApi.patch(customerId, activity.id, body)
        : customerActivitiesApi.create(customerId, body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Activity updated' : 'Activity created');
      // Custom activities are products; the timesheet picker reads the products
      // cache, so invalidate both this customer's list and the products cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.customerActivities.list(customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      onOpenChange(false);
    },
    onError: (err) => handleProductMutationError(form, err),
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit activity' : 'Add activity'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Edit this customer-specific activity. Changes apply immediately.'
                : 'Create an activity available only to this customer.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto px-4">
              <ProductFormFields form={form} hideInternalToggle />
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create activity'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
